import { isString } from 'es-toolkit/compat';
import {
  ExperimentResult,
  FlowClassName,
  OptionsEntry,
  ResultColorMap,
  ResultType,
} from 'interfaces/experimentResults';
import { Job } from 'interfaces/job';

export enum HeatmapType {
  Dzi = 'dzi',
  Pmt = 'pmt',
  GeoJson = 'geojson',
  PmtLayer = 'pmt-layer',
  Parquet = 'parquet',
}

export enum ParquetLayerType {
  MarkerPositivity = 'marker positivity',
  MarkerProbability = 'marker probability',
  CellClassification = 'cell classification',
  CellIntensityClassification = 'cell intensity classification',
  AreaRegionClassification = 'area region classification',
  ClusterIdClassification = 'cluster id classification',
  CellClassificationDensities = 'cell classification densities',
  Quantification = 'quantification',
  Other = 'other',
}

export enum ResultOverlaySection {
  CellSegmentation = 'cell-segmentation',
  SpotPositivity = 'spot-positivity',
  Scores = 'scores',
}

export enum SecondaryAnalysisStatus {
  None = 'none',
  PrimaryResult = 'primary-result',
  SecondaryResult = 'secondary-result',
  SecondaryResultWithMissingParent = 'secondary-result-with-missing-parent',
}

export interface PresignedPresentationInfoUrls {
  presigned_get_url?: string;
  unsigned_url?: string;
  presigned_head_url?: string;
}

export const heatmapUrlToPresignedGetUrl = (
  heatmapUrl: string | PresignedPresentationInfoUrls,
  assumeStringHeatmapUrlIsPresigned?: boolean
): string | undefined =>
  !isString(heatmapUrl)
    ? heatmapUrl?.presigned_get_url || undefined
    : assumeStringHeatmapUrlIsPresigned
    ? heatmapUrl || undefined
    : undefined;

export const heatmapUrlToPresignedHeadUrl = (heatmapUrl: string | PresignedPresentationInfoUrls): string | undefined =>
  !isString(heatmapUrl) ? heatmapUrl?.presigned_head_url || heatmapUrl?.presigned_get_url || undefined : undefined;

interface BaseFeatureMetadata {
  key?: string;
  id: string;
  job?: Partial<Job>;
  value?: number | string;
  heatmapType?: HeatmapType;
  parquetLayerType?: ParquetLayerType;
  columnName?: string;
  min?: number;
  max?: number;
  heatmapOption?: OptionsEntry;
  columnType?: OptionsEntry['type'];
  heatmapUrl?: string | PresignedPresentationInfoUrls;
  cellsDataUrl?: string;
  displayName?: string;
  orchestrationId?: string;
  numFeatures?: number;
  createdAt?: string;
  deletedAt?: string;
  deletedBy?: string;
  approved?: boolean;
  internallyApproved?: boolean;
  experimentResultId?: number;
  flowClassName?: FlowClassName;
  resultType?: ResultType;
  color?: string | ResultColorMap;
  options?: ExperimentResult['options'];
  primaryRunOrchestrationId?: string;
  registeredFromStainTypeIndex?: number;
  visualizationId?: string; // From brackets, e.g. v:1 => '1', v:12 => '12', etc'
  overlaySection?: ResultOverlaySection;
  segmentationCompartment?: 'membrane' | 'nucleus';
  segmentationDilation?: number;
  positivityProbeId?: string;
  positivityProbeDisplayName?: string;
  positivityCompartment?: string;
  positivityDilation?: number;
  positivityThreshold?: number;
  positivityCellClass?: string;
  positivityCellClassDisplayName?: string;
  positivityCellClassColumn?: string;
  scoreMarkerId?: string;
  scoreMarkerDisplayName?: string;
  scoreContext?: string;
}

export interface NestedHeatmap extends BaseFeatureMetadata {
  nestedItems?: NestedHeatmap[];
  secondaryResults?: null | undefined;
  secondaryAnalysisStatus?: undefined | null;
}

export interface FeatureMetadataBeforeSecondaryAnalysisGrouping extends BaseFeatureMetadata {
  nestedItems?: NestedHeatmap[];
}

export interface FeatureMetadataWithSingleRun extends FeatureMetadataBeforeSecondaryAnalysisGrouping {
  primaryRunOrchestrationId: undefined | null;
  secondaryResults?: null | undefined;
  secondaryAnalysisStatus: SecondaryAnalysisStatus.None;
}

export interface FeatureMetadataWithMissingParent extends FeatureMetadataBeforeSecondaryAnalysisGrouping {
  primaryRunOrchestrationId: string;
  secondaryAnalysisStatus: SecondaryAnalysisStatus.SecondaryResultWithMissingParent;
  secondaryResults?: null | undefined;
}

export interface FeatureMetadataSecondaryAnalysisEntry extends FeatureMetadataBeforeSecondaryAnalysisGrouping {
  primaryRunOrchestrationId: string;
  secondaryResults?: null | undefined;
  secondaryAnalysisStatus: SecondaryAnalysisStatus.SecondaryResult;
}

export interface FeatureMetadataWithSecondaryAnalysisFlow extends FeatureMetadataBeforeSecondaryAnalysisGrouping {
  secondaryResults?: FeatureMetadataSecondaryAnalysisEntry[];
  primaryRunOrchestrationId: null | undefined;
  secondaryAnalysisStatus: SecondaryAnalysisStatus.PrimaryResult;
}

// This is the final type used for both heatmaps and features after parsing
export type FeatureMetadata =
  | FeatureMetadataWithSingleRun
  | FeatureMetadataWithSecondaryAnalysisFlow
  | FeatureMetadataWithMissingParent
  | FeatureMetadataSecondaryAnalysisEntry
  | NestedHeatmap;

export interface Results {
  publishedResults: FeatureMetadata[];
  internalResults: { [key: string]: FeatureMetadata[] };
  deletedResults?: FeatureMetadata[];
}

export interface ParsedResults {
  heatmaps: Results;
  features: Results;
  internalHeatmaps?: { [key: string]: FeatureMetadata[] };
}

export const experimentResultToGroup = (result: FeatureMetadata) => result.resultType || result.flowClassName;

export const parseBaseFeatureMetadataFromExperimentResult = (
  experimentResult: ExperimentResult,
  keyPrefix: string // keyPrefix is used to differentiate between different types of features / heatmaps for the same experiment result
): FeatureMetadataBeforeSecondaryAnalysisGrouping => ({
  id: `[${experimentResult.experimentResultId}]-${keyPrefix}`,
  key: `${keyPrefix}[${
    experimentResult.primaryRunOrchestrationId ||
    experimentResult.orchestrationId ||
    experimentResult.experimentResultId
  }]`,
  job: experimentResult.job,
  displayName: experimentResult?.job?.name || experimentResult.name || experimentResult?.job?.id || '',
  experimentResultId: experimentResult.experimentResultId,
  orchestrationId: experimentResult.orchestrationId,
  numFeatures: experimentResult.numFeatures,
  createdAt: experimentResult.createdAt,
  approved: Boolean(experimentResult.approved),
  internallyApproved: Boolean(experimentResult.internallyApproved),
  flowClassName: experimentResult.flowClassName,
  resultType: experimentResult.resultType,
  primaryRunOrchestrationId: experimentResult.primaryRunOrchestrationId,
  deletedAt: experimentResult.deletedAt,
  deletedBy: experimentResult.deletedBy,
  options: experimentResult.options,
});
